import { useCallback, useEffect, useState } from "react";
import { SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";
import { NumStepper } from "@/components/ui/num-stepper";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export const AdminCalculator = ({ client, api, isAdmin }) => {
  const [form, setForm] = useState({ f1: 50, width: 100, f2: 50, thickness: 1.6, length_ft: 8, qty: 1 });
  const [whatIf, setWhatIf] = useState({ sheet_rate: "", overhead: "", margin: "" });
  const [advOpen, setAdvOpen] = useState(false);
  const [result, setResult] = useState(null);

  const calc = useCallback(async () => {
    try {
      const what_rates = {};
      for (const k of ["sheet_rate", "overhead", "margin"]) {
        if (whatIf[k] !== "") what_rates[k] = num(whatIf[k]);
      }
      const { data } = await client.post(`${api}/qv2/preview`, {
        items: [{
          f1: num(form.f1), width: num(form.width), f2: num(form.f2),
          thickness: num(form.thickness), length_ft: num(form.length_ft),
          qty: Math.max(1, Math.round(num(form.qty)) || 1),
          perforation_loss: 0, price_format: "per_piece",
        }],
        what_rates: Object.keys(what_rates).length ? what_rates : null,
      });
      setResult(data);
    } catch {
      setResult(null);
    }
  }, [client, api, form, whatIf]);

  useEffect(() => {
    const t = setTimeout(calc, 250);
    return () => clearTimeout(t);
  }, [calc]);

  const item = result?.items?.[0];
  const field = (key, label, testid) => (
    <NumStepper key={key} label={label} testid={testid} step="any" value={form[key]} onChange={(v) => setForm({ ...form, [key]: v })} />
  );

  const row = (label, value, testid, bold = false) => (
    <div className={`flex items-center justify-between border-b border-white/10 py-3 ${bold ? "font-heading font-semibold" : ""}`}>
      <span className="font-body text-sm text-white/80">{label}</span>
      <span data-testid={testid} className={`font-mono text-sm ${bold ? "text-[#7FB3E8]" : "text-white"}`}>{value}</span>
    </div>
  );

  return (
    <div data-testid="calculator-panel" className="grid grid-cols-1 lg:grid-cols-12 gap-6 max-w-5xl">
      <div className="lg:col-span-5 bg-steel border border-white/10 p-6 md:p-8">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-4">Tray Sign (mm)</p>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {field("f1", "F1", "calc-f1")}
          {field("width", "Width", "calc-width")}
          {field("f2", "F2", "calc-f2")}
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6">
          {field("thickness", "Thk (mm)", "calc-thickness")}
          {field("length_ft", "Len (ft)", "calc-length")}
          {field("qty", "Qty", "calc-qty")}
        </div>

        <button
          data-testid="calc-advanced-toggle"
          onClick={() => setAdvOpen(!advOpen)}
          className="w-full border border-dashed border-white/15 p-4 text-left hover:border-[#7FB3E8]/50 transition-colors duration-300"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SlidersHorizontal size={14} className="text-[#7FB3E8]" />
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke">Advanced · What-If Rates</span>
            </div>
            {advOpen ? <ChevronUp size={14} className="text-smoke" /> : <ChevronDown size={14} className="text-smoke" />}
          </div>
          <p className="mt-1.5 font-body text-xs text-smoke">Try a different sheet rate / overhead / margin without touching global Rates.</p>
        </button>

        {advOpen && (
          <div className="grid grid-cols-3 gap-3 mt-4" data-testid="calc-whatif-panel">
            {[["sheet_rate", "Sheet ₹/kg"], ["overhead", "Overhead %"], ["margin", "Margin %"]].map(([k, lbl]) => (
              <NumStepper key={k} label={lbl} testid={`calc-whatif-${k}`} placeholder="auto" value={whatIf[k]} onChange={(v) => setWhatIf({ ...whatIf, [k]: v })} />
            ))}
          </div>
        )}
      </div>

      <div className="lg:col-span-7 bg-steel border border-white/10 p-6 md:p-8">
        <div className="flex flex-wrap gap-8 mb-6">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Unit Weight</p>
            <p data-testid="calc-unit-weight" className="font-heading font-semibold text-3xl md:text-4xl text-white mt-1">
              {item ? item.kg_per_piece : "—"} kg
            </p>
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Selling / Piece</p>
            <p data-testid="calc-selling-price" className="font-heading font-semibold text-3xl md:text-4xl text-[#7FB3E8] mt-1">
              {item ? fmt(item.unit_price) : "—"}
            </p>
          </div>
        </div>
        {item && (
          <div data-testid="calc-breakdown">
            {row("Developed width", `${item.dev_width_mm?.toFixed(2)} mm`, "calc-dev-width")}
            {row("Cut length", `${(item.cut_length_m * 1000)?.toLocaleString("en-IN", { maximumFractionDigits: 2 })} mm`, "calc-cut-length")}
            {row("Total weight", `${item.total_kg} kg`, "calc-total-weight")}
            {isAdmin && item.material != null && row("Material cost", fmt(item.material), "calc-material")}
            {isAdmin && item.overhead != null && row("Overhead", fmt(item.overhead), "calc-overhead")}
            {isAdmin && item.margin != null && row("Margin", fmt(item.margin), "calc-margin")}
            {row("Line total (excl. GST)", fmt(item.line_total), "calc-line-total", true)}
          </div>
        )}
      </div>
    </div>
  );
};
